import { useLocationPathname } from '@rocketlane/rocket-router';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { BehaviorSubject, concat, merge, of, Subject, filter, map, shareReplay, switchMap } from 'rxjs';
import { AccountId, UserId } from 'api/models';
import { notifications } from 'api/notifications';
import { Action, EventType, ObjectType, pushes, UserChannel } from 'api/pushes';
import { queryClient } from 'queries';
import { NOTIFICATION_DATA_QUERY_KEY, useAccountDataQuery, useNotificationDataQuery } from 'queries/account';
import { logger } from 'services';
const Authorized = (userId, accountId)=>[
        'authorized',
        userId,
        accountId
    ];
// BehaviourSubject is a hot observable that emits the last value to new subscribers
// and the current value to all subscribers when a new value is emitted.
// https://rxjs.dev/guide/subject#behaviorsubject
const _auth$ = new BehaviorSubject(null);
const _lastSeenAt$ = new BehaviorSubject(null);
// Subject is a hot observable that emits the current value to all subscribers
// when a new value is emitted. It does not emit the last value to new subscribers.
// https://rxjs.dev/guide/subject#subject
const _seen$ = new Subject();
// Observable of the last time the user has seen notifications.
const lastSeenAt$ = _lastSeenAt$.asObservable().pipe(filter((x)=>x != null), map((x)=>x), shareReplay(1));
// Observable of authorization status.
// This is a hot observable that emits the current authorization status
// and all future authorization status changes.
// The current authorization status is emitted immediately.
// The current authorization status is null if the user is not authorized.
const auth$ = _auth$.asObservable().pipe(filter((x)=>x != null), map((x)=>x), // Replay the last authorization status. more on shareReplay here https://www.learnrxjs.io/learn-rxjs/operators/multicasting/sharereplay
shareReplay(1));
// initailaize the auth observable. This will emit the current authorization status immediately.
export const initialize = (userId, accountId)=>{
    if (_auth$.value !== null) {
        return;
    }
    _auth$.next(Authorized(userId, accountId));
};
// merge the last seen at observable with the seen observable.
export const handleLastSeenAtSubscription = ()=>merge(auth$.pipe(switchMap(()=>notifications.lastSeenAt())), // when the user sees notifications, update the last seen at observable.
    _seen$.pipe(// switchmap cancels the previous request and starts a new one.
    // https://www.learnrxjs.io/learn-rxjs/operators/transformation/switchmap
    switchMap(()=>notifications.seen()), switchMap(()=>concat(of(moment()), notifications.lastSeenAt())))).subscribe((at)=>_lastSeenAt$.next(at), (error)=>{
        logger.error('fetching last seen at failed', {
            error
        });
    });
// export seen observable to be used in useNotificationsStore
export const seen = (at)=>{
    _seen$.next(at);
};
// export lastSeenAt observable to be used in other files.
export const lastSeenAt = ()=>lastSeenAt$;
/**
 * custom hook to get the pending notifications count.
 * The pending notifications count will be updated inside the useAccountDataQuery hook.
 * This hook will return the pending notifications count from the queryClient cache.
 * every time the queryClient cache is updated via websocket, The pending notifications count will be incremented
 * @param  {}
 * @returns [number, () => void]
 */ export const usePending = ()=>{
    var _accountData_userProfile;
    // get the current authorization status.
    const { data: accountData } = useAccountDataQuery();
    const [newNotfication, setNewNotificaiton] = useState(accountData === null || accountData === void 0 ? void 0 : accountData.newNotificationAvailable);
    useEffect(()=>{
        setNewNotificaiton(accountData === null || accountData === void 0 ? void 0 : accountData.newNotificationAvailable);
    }, [
        accountData === null || accountData === void 0 ? void 0 : accountData.newNotificationAvailable
    ]);
    const { data: notificationData } = useNotificationDataQuery();
    const { accountId } = (accountData === null || accountData === void 0 ? void 0 : accountData.account) || {};
    const { userId } = (accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile = accountData.userProfile) === null || _accountData_userProfile === void 0 ? void 0 : _accountData_userProfile.user) || {};
    const pathname = useLocationPathname();
    useEffect(()=>{
        function notificastionHandler() {
            // update the query client cache with the pending notifications count.
            // set the newNotificationAvailable property to false after the first render
            // the newNotificationAvailable should be used only for the firrst render
            // this way we can avoid the inital api call for lastSeenAt
            if ((notificationData === null || notificationData === void 0 ? void 0 : notificationData.pendingNotifications) === 0 || pathname.includes('notifications')) {
                queryClient.setQueryData(NOTIFICATION_DATA_QUERY_KEY, {
                    // increment the pending notifications count by 1 when a new notification is received.
                    pendingNotifications: ((notificationData === null || notificationData === void 0 ? void 0 : notificationData.pendingNotifications) || 0) + 1,
                    // set the newNotificationAvailable property to false after the first render
                    newNotificationAvailable: false
                });
            }
        }
        // subscribe to notification channel
        // check if subscription is already created
        if (accountId && userId) {
            EventType(Action.Create, ObjectType.Notification);
            pushes(UserChannel(AccountId(accountId), UserId(userId)), EventType(Action.Create, ObjectType.Notification)).subscribe(notificastionHandler);
        }
    }, [
        accountId,
        userId,
        notificationData,
        pathname
    ]);
    // expose a methiod to reset the pending notifications count.
    const resetPending = useCallback(()=>{
        queryClient.setQueryData(NOTIFICATION_DATA_QUERY_KEY, {
            pendingNotifications: 0,
            newNotificationAvailable: false
        });
        setNewNotificaiton(false);
    }, []);
    // On page load, the pending notification clunt should be taken from newNotificationAvailable property in init call
    if (newNotfication) {
        return [
            1,
            resetPending
        ];
    }
    // return the pending notifications count and the resetPending method
    const pendingNotifications = (notificationData === null || notificationData === void 0 ? void 0 : notificationData.pendingNotifications) || 0;
    return [
        pendingNotifications,
        resetPending
    ];
};
